import PlaceIcon from "@mui/icons-material/Place";
import DeleteIcon from "@mui/icons-material/Delete";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import { Typography } from "@mui/material";
import { useMemo } from "react";
import { ReactCountryFlag } from "react-country-flag";
import { ColSubModel } from "../components/GenericTableRow";
import { formatCoordinates, formatDateWithTimezone } from "./helpers";
import {
	Portfolio,
	Site,
	isPortfolio,
} from "../../features/sites/sites.module";
import { formatProxyGenerationStatus } from "../utils/formatProxyGenerationStatus";
import { FlashUpdateTableCell } from "../components/FlashUpdateTableCell";
import { SiteKnownStatusField } from "../components/SiteKnownStatusField";
import { SiteTechnicalDetails } from "../components/SiteTechnicalDetails";
import PicColumn from "../components/PicColumn";

export interface TenderFormSiteTableSubModelParams {
	showActions: boolean;
	showRefreshCluster?: boolean;
	deleteAction: (parent: any, m: Site) => void;
}

export const useTenderFormSiteTableSubModel = (
	params: TenderFormSiteTableSubModelParams
) => {
	const { showActions, deleteAction, showRefreshCluster = true } = params;
	const subRowModel: ColSubModel<Site>[] = useMemo(() => {
		const rows = [
			{
				id: "delete",
				header: () => "Actions",
				xs: 1,
				hidden: !showActions,
				accessor: (m: Site, index: number, parent: any) => (
					<>
						<Typography
							component="span"
							sx={{
								justifyContent: "center",
								display: "flex",
								width: "145px",
							}}
						>
							<DeleteIcon
								sx={{ cursor: "pointer" }}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									deleteAction(parent, m);
								}}
							/>
						</Typography>
					</>
				),
			},
			{
				id: "id",
				header: () => "Id",
				xs: 1,
				hidden: true,
				accessor: (m: Site) => m.id,
			},
			{
				id: "asset_id",
				header: () => "TPR Id",
				xs: 1,
				hidden: false,
				accessor: (m: Site) => (isPortfolio(m) ? "-" : m.asset_id),
			},
			{
				id: "name",
				header: () => "Name",
				xs: 2,
				accessor: (m: Site) =>
					m.specific_name ? m.specific_name : m.name,
			},
			{
				id: "type",
				header: () => "Type",
				xs: 1,
				accessor: (m: Site) => (
					<>
						{!!m.children && (
							<>
								<BookmarksIcon />
								{" Portfolio"}
							</>
						)}
						{!m.children && (
							<>
								{" "}
								<PlaceIcon></PlaceIcon>
								{" Site"}
							</>
						)}
					</>
				),
			},
			{
				id: "country",
				header: () => "Country",
				xs: 0.5,
				accessor: (m: Site) => (
					<>
						<ReactCountryFlag
							countryCode={m.country ?? ""}
							svg
							title={m.country ?? ""}
						/>{" "}
						{m.country}
					</>
				),
			},
			{
				id: "cluster",
				header: () => "Cluster",
				xs: 2,
				accessor: (m: Site) => {
					let clusterName = "";
					if ("cluster_names" in m) {
						clusterName = m.cluster_names ?? "";
					}
					return (
						<FlashUpdateTableCell
							field={clusterName}
							isUpdate={m.isNewUpdate}
							align="left"
							componentType="box"
							title={clusterName}
							sx={{
								border: "0 !important",
								padding: "0 !important",
								width: "160px !important",
								textOverflow: "ellipsis",
								overflow: "hidden",
								whiteSpace: "nowrap",
							}}
						>
							<Typography
								variant="subtitle2"
								noWrap
								sx={{
									border: "0 !important",
									padding: "0 !important",
									width: "100% !important",
								}}
							>
								{clusterName}
							</Typography>
						</FlashUpdateTableCell>
					);
				},
			},

			{
				id: "clusterstatus",
				header: () => "Cl. Status",
				xs: 1,
				accessor: (m: Site) => {
					return (
						<FlashUpdateTableCell
							field={formatProxyGenerationStatus(
								m.proxy_generation_status,
								m.proxy_generation_errors
							)}
							isUpdate={m.isNewUpdate}
							align="left"
							sx={{
								border: "0 !important",
								padding: "0 !important",
							}}
						>
							{formatProxyGenerationStatus(
								m.proxy_generation_status,
								m.proxy_generation_errors
							)}
						</FlashUpdateTableCell>
					);
				},
			},
			{
				id: "clusterdate",
				header: () => "Cl. Date",
				xs: 1.6,
				kind: "datetime" as const,
				extractData: (m: Site) => {
					if (m?.proxy_generation_date) {
						return m.proxy_generation_date;
					}
					return "";
				},
				accessor: (m: Site) => {
					const subTableProxyGenerationDate = formatDateWithTimezone(
						m.proxy_generation_date,
						"dd/MM/yyyy, HH:mm"
					);
					return (
						<FlashUpdateTableCell
							field={subTableProxyGenerationDate}
							isUpdate={m.isNewUpdate}
							align="left"
							sx={{
								border: "0 !important",
								padding: "0 !important",
							}}
						>
							{subTableProxyGenerationDate}
						</FlashUpdateTableCell>
					);
				},
			},
			{
				id: "techno",
				header: () => "Technology",
				xs: 1,
				accessor: (m: Site | Portfolio) =>
					"techno" in m && m?.techno?.name,
			},
			{
				id: "pic_id",
				header: () => "PIC",
				xs: 1,
				accessor: (m: Site | Portfolio) => <PicColumn item={m}/>
			},
			{
				id: "tso",
				header: () => "Tso",
				xs: 1,
				accessor: (m: Site | Portfolio) =>
					isPortfolio(m) ? "-" : m?.tso,
			},
			{
				id: "capa",
				header: () => "Installed capacity",
				xs: 1,
				accessor: (m: Site | Portfolio) => (
					<>
						{"installed_capacity_mw" in m &&
							(m.installed_capacity_mw ?? "")}
						{!("installed_capacity_mw" in m)
							? (m as Portfolio).children.reduce(
									(t: number, s: Site) =>
										t + (s.installed_capacity_mw ?? 0),
									0
							  )
							: ""}
					</>
				),
			},
			{
				id: "max_injection",
				header: () => "Max Grid Injection",
				xs: 1.2,
				accessor: (m: Site | Portfolio) =>
					isPortfolio(m) ? "-" : m.max_grid_injection_mw || "-",
			},
			{
				id: "parkage",
				header: () => "Park age",
				xs: 1,
				accessor: (m: Site | Portfolio) => m?.park_age,
			},
			{
				id: "tech",
				header: () => "Technical details",
				xs: 2,
				accessor: (m: Site | Portfolio) => (
					<SiteTechnicalDetails site={m} />
				),
			},
			{
				id: "lat",
				header: () => "Latitude",
				xs: 1,
				accessor: (m: Site | Portfolio) =>
					isPortfolio(m) ? "" : formatCoordinates(4, m).latitude,
			},
			{
				id: "long",
				header: () => "Longitude",
				xs: 1,
				accessor: (m: Site | Portfolio) =>
					isPortfolio(m) ? "" : formatCoordinates(4, m).longitude,
			},
			{
				id: "is_curtailable",
				header: () => "Is Curtailable",
				xs: 1,
				accessor: (m: Site | Portfolio) => {
					if (isPortfolio(m)) {
						return "";
					} else {
						switch (m.is_curtailable) {
							case true:
								return "Yes";
							case false:
								return "No";
							case null:
								return "N/A";
						}
					}
				},
			},
			{
				id: "curtailability_subsidy_price",
				header: () => "Curtailability Subsidy Price",
				xs: 1,
				accessor: (m: Site | Portfolio) =>
					isPortfolio(m) || m.curtailability_subsidy_price === null || m.curtailability_subsidy_price === undefined || !m.is_curtailable
						? ""
						: m.curtailability_subsidy_price,
				sortable: true,
			},
			{
				id: "known_status",
				header: () => "Known status",
				xs: 1.5,
				accessor: (m: Site | Portfolio) => {
					return (
						<Typography variant="subtitle2">
							{!isPortfolio(m) && (
								<SiteKnownStatusField site={m} />
							)}
						</Typography>
					);
				},
			},
			{
				id: "customer_p50",
				header: () => "Customer P50",
				xs: 1,
				accessor: () => <></>,
			},

			{
				id: "cluster_p50",
				header: () => "Cluster P50",
				xs: 1,
				accessor: () => <></>,
			},
			{
				id: "#pricings",
				header: () => "Pricings",
				xs: 1,
				accessor: (m: Site | Portfolio) =>
					isPortfolio(m) ? null : <></>,
			},
			{
				id: "actions",
				header: () => " ",
				hidden: !showRefreshCluster,
				xs: 0.5,
				accessor: (m: Site | Portfolio) =>
					isPortfolio(m) ? null : <></>,
			},
		];
		return rows;
	}, []);

	return subRowModel;
};
